import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCaseService } from '../services/createCase.service';
import TriagePanel from '../components/diagnostics/TriagePanel';

const LOCATION_OPTIONS = [
  { value: '', label: '- Select -' },
  { value: 'Home/Office', label: 'Home/Office' },
  { value: 'On Road', label: 'On Road' },
];

const initialFormState = () => ({
  status: 'Draft',
  causeOfCallId: '',
  location: '',
  vehicle_id: '',
  policy_id: '',
  callReceivedDate: '',
  kmsAtBreakdown: '',
  referenceId: '',
  description: '',
});

const CreateCase = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const customerId = location.state?.customer?.id;
  const schemeId = location.state?.customer?.scheme_id;
  const policyId = location.state?.customer?.policy_id;
  const vehicleId = location.state?.customer?.vehicle_id;
  const [form, setForm] = useState(initialFormState());
  const [saving, setSaving] = useState(false);
  const [causeOptions, setCauseOptions] = useState([]);
  const [loadingCauses, setLoadingCauses] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [policyDetails, setPolicyDetails] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [showPolicyLimitDialog, setShowPolicyLimitDialog] = useState(false);
  const [pendingCaseData, setPendingCaseData] = useState(null);
  const [error, setError] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isOthersSelected, setIsOthersSelected] = useState(false);
  const [triageSessionId, setTriageSessionId] = useState(null);

  useEffect(() => {
    const fetchCauses = async () => {
      if (!schemeId) {
        setCauseOptions([{ id: '', name: '- Select -' }]);
        setLoadingCauses(false);
        return;
      }

      setLoadingCauses(true);
      try {
        const response = await createCaseService.getSchemeCauseOfCalls(schemeId, { is_active: true });
        setCauseOptions([
          { id: '', name: '- Select -' },
          ...(Array.isArray(response.causes) ? response.causes : [])
        ]);
      } catch (error) {
        console.error('Error fetching cause of calls:', error);
        setCauseOptions([{ id: '', name: '- Select -' }]);
      } finally {
        setLoadingCauses(false);
      }
    };
    fetchCauses();
  }, [schemeId]);

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!customerId) return;

      setLoadingCustomer(true);
      try {
        const response = await createCaseService.getCustomerDetails(customerId);
        if (response.success) {
          setCustomerData({
            customer: response.customer,
            vehicles: response.vehicles,
            policies: response.policies,
            recent_cases: response.recent_cases
          });

          // Auto-select if only one vehicle
          if (response.vehicles?.length === 1) {
            setForm(prev => ({ ...prev, vehicle_id: response.vehicles[0].id }));
          }

          // Auto-select if only one policy
          if (response.policies?.length === 1) {
            setForm(prev => ({ ...prev, policy_id: response.policies[0].id }));
          }
        } else {
          console.error('Error fetching customer data:', response.error);
          setCustomerData(null);
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setCustomerData(null);
      } finally {
        setLoadingCustomer(false);
      }
    };
    fetchCustomerData();
  }, [customerId]);

  // Auto-select policy when vehicle is auto-selected
  useEffect(() => {
    if (customerData?.vehicles?.length === 1 && customerData?.policies?.length > 0 && form.vehicle_id && !form.policy_id) {
      const selectedVehicle = customerData.vehicles[0];
      const matchingPolicy = customerData.policies.find(p =>
        p.vehicle && p.vehicle.registration_number === selectedVehicle.registration_number
      );
      if (matchingPolicy) {
        setForm(prev => ({ ...prev, policy_id: matchingPolicy.id }));
      }
    }
  }, [customerData, form.vehicle_id, form.policy_id]);

  // Fetch vehicle details if vehicle ID is provided
  useEffect(() => {
    const fetchVehicleDetails = async () => {
      if (!vehicleId) return;

      setLoadingVehicle(true);
      try {
        const response = await createCaseService.getVehicleDetails(vehicleId);
        if (response.success) {
          setVehicleDetails(response);
          // Auto-select the vehicle
          setForm(prev => ({ ...prev, vehicle_id: vehicleId }));
        } else {
          console.error('Error fetching vehicle details:', response.error);
          setVehicleDetails(null);
        }
      } catch (error) {
        console.error('Error fetching vehicle details:', error);
        setVehicleDetails(null);
      } finally {
        setLoadingVehicle(false);
      }
    };
    fetchVehicleDetails();
  }, [vehicleId]);

  // Fetch policy details if policy ID is provided
  useEffect(() => {
    const fetchPolicyDetails = async () => {
      if (!policyId) return;

      setLoadingPolicy(true);
      try {
        const response = await createCaseService.getPolicyDetails(policyId);
        if (response.success) {
          setPolicyDetails(response);
          // Auto-select the policy
          setForm(prev => ({ ...prev, policy_id: policyId }));
        } else {
          console.error('Error fetching policy details:', response.error);
          setPolicyDetails(null);
        }
      } catch (error) {
        console.error('Error fetching policy details:', error);
        setPolicyDetails(null);
      } finally {
        setLoadingPolicy(false);
      }
    };
    fetchPolicyDetails();
  }, [policyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Check if "Others" is selected for cause of call
    if (name === 'causeOfCallId') {
      const selectedCause = causeOptions.find(opt => opt.id === parseInt(value));
      
      // Check by both code and name for "Others"
      const isOthers = selectedCause && (
        selectedCause.code === 'OTHERS' || 
        selectedCause.name === 'Others' ||
        selectedCause.name === 'OTHERS'
      );
      
      setIsOthersSelected(isOthers);
      if (!isOthers) {
        setOtherReason(''); // Clear other reason if not "Others"
      }
    }
  };

  // Utility function to detect policy limit exceeded errors
  const isPolicyLimitExceededError = (error) => {
    try {
      // Check if error object exists
      if (!error) {
        console.log('Error object is null or undefined');
        return false;
      }

      // Must be a 400 status code
      console.log('Error status:', error.status);
      if (error.status !== 400) return false;

      // Must have an error message

      if (!error.errorData) {
        console.log('Error message is missing');
        return false;
      }

      const errorMessage = error.errorData.detail.toLowerCase();
      console.log('errorMessage:', errorMessage);
      // Check for the specific error patterns from PolicyLimitExceededException
      const policyLimitPatterns = [
        'maximum claims limit',                    // Primary pattern from backend
        'has reached maximum claims limit',       // Full phrase match
        'policy limit exceeded',                  // Fallback pattern
        'policy.*limit.*exceeded',
        'no active policy found for the selected vehicle'           // Regex-like pattern
      ];

      return policyLimitPatterns.some(pattern => {
        try {
          if (pattern.includes('.*')) {
            // Simple regex pattern matching
            const regex = new RegExp(pattern);
            return regex.test(errorMessage);
          }
          return errorMessage.includes(pattern);
        } catch (regexError) {
          console.warn('Regex pattern error:', regexError);
          return false;
        }
      });
    } catch (err) {
      console.error('Error in isPolicyLimitExceededError:', err);
      return false;
    }
  };

  const dummyFn = (p1) => {
    console.log('In DummyFn p1:', p1);
  }

  const handleSubmit = async (e, ignorePolicyLimit = false) => {
    e.preventDefault();

    console.log('In handleSubmit');

    // Guard clause: don't execute if already saving
    if (saving) {
      return;
    }

    setSaving(true);
    setError('');

    // Validate other reason if "Others" is selected
    if (isOthersSelected && !otherReason.trim()) {
      setError('Please specify the other reason.');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        customer_id: customerId || 0,
        vehicle_id: vehicleDetails?.vehicle?.id || vehicleId || form.vehicle_id || 0,
        vehicle_registration: vehicleDetails?.vehicle?.registration_number || '',
        client_id: location.state?.customer?.client_id || 0,
        client_scheme_id: schemeId || 0,
        scheme_cause_of_call_id: form.causeOfCallId || 0,
        description: form.description || '',
        ignorePolicyLimitFlag: ignorePolicyLimit,
      };

      const response = await createCaseService.createCase(payload);
      
      // If "Others" was selected, create a case note with the custom reason
      console.log('Checking for Others case note creation:', { isOthersSelected, otherReason: otherReason.trim() });
      if (isOthersSelected && otherReason.trim()) {
        console.log('Creating case note for Others reason:', otherReason.trim());
        try {
          await createCaseService.addCaseNote(response.case.case.id, {
            note_type: 'Other cause of call',
            note_text: otherReason.trim(),
            is_internal: false
          });
          console.log('Case note created successfully');
        } catch (noteError) {
          console.error('Error creating case note for other reason:', noteError);
          // Don't fail the entire operation if note creation fails
        }
      }
      
      navigate('/add-service', { state: { case_id: response.case.case.id, service_id: null, triageSessionId } });
    } catch (err) {
      console.error('Error creating case:', err);
      console.error('Error type:', typeof err);
      console.error('Error constructor:', err?.constructor?.name);
      console.error('Error status:', err?.status);
      console.error('Error message:', err?.message);
      console.error('Error as JSON:', JSON.stringify(err, null, 2));

      dummyFn(err);
      // Use the utility function for cleaner code
      if (isPolicyLimitExceededError(err)) {
        console.log('Policy limit exceeded error pattern detected');
        setPendingCaseData({
          customer_id: customerId || 0,
          vehicle_id: vehicleDetails?.vehicle?.id || vehicleId || form.vehicle_id || 0,
          vehicle_registration: vehicleDetails?.vehicle?.registration_number || '',
          client_id: location.state?.customer?.client_id || 0,
          client_scheme_id: schemeId || 0,
          scheme_cause_of_call_id: form.causeOfCallId || 0,
          description: form.description || '',
          otherReason: otherReason.trim(),
          isOthersSelected: isOthersSelected,
        });
        setShowPolicyLimitDialog(true);
        setSaving(false);
        return;
      }

      // Handle other errors
      setError(err.message || 'An error occurred while creating the case. Please try again.');
      setSaving(false);
    }
  };



  const handlePolicyLimitConfirm = async () => {
    setShowPolicyLimitDialog(false);
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...pendingCaseData,
        ignorePolicyLimitFlag: true,
      };

      const response = await createCaseService.createCase(payload);
      
      // If "Others" was selected, create a case note with the custom reason
      console.log('Policy limit override - Checking for Others case note creation:', { isOthersSelected, otherReason: otherReason.trim() });
      if (isOthersSelected && otherReason.trim()) {
        console.log('Policy limit override - Creating case note for Others reason:', otherReason.trim());
        try {
          await createCaseService.addCaseNote(response.case.case.id, {
            note_type: 'Other cause of call',
            note_text: otherReason.trim(),
            is_internal: false
          });
          console.log('Policy limit override - Case note created successfully');
        } catch (noteError) {
          console.error('Error creating case note for other reason:', noteError);
          // Don't fail the entire operation if note creation fails
        }
      }
      
      navigate('/add-service', { state: { case_id: response.case.case.id, service_id: null, triageSessionId } });
    } catch (err) {
      console.error('Error creating case with policy limit override:', err);
      setError(err.message || 'An error occurred while creating the case. Please try again.');
      setSaving(false);
    }
  };

  const handlePolicyLimitCancel = () => {
    setShowPolicyLimitDialog(false);
    setPendingCaseData(null);
    setSaving(false);
  };

  // Find the selected vehicle object
  const selectedVehicle = vehicleDetails?.vehicle || customerData?.vehicles?.find(v => String(v.id) === String(form.vehicle_id));

  // Find the selected policy object
  const selectedPolicy = policyDetails?.policy || customerData?.policies?.find(p => String(p.id) === String(form.policy_id));

  if (!customerId) {
    return (
      <div className="w-full mx-auto mt-10 p-6 bg-white mrgall rounded-lg border bxshadowhd">
        <div className="text-center text-red-600">No customer data provided</div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto mt-10 p-6 bg-white mrgall rounded-lg border bxshadowhd">
      <h2 className="text-2xl font-semibold mb-8">Create Case</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-1 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {schemeId && (
          <TriagePanel
            schemeId={schemeId}
            context={{
              vehicle: {
                fuelType: vehicleDetails?.vehicle?.fuel_type,
                make: vehicleDetails?.vehicle?.make,
                model: vehicleDetails?.vehicle?.model,
              },
            }}
            onOutcome={(res) => {
              setTriageSessionId(res.sessionId);
              const pre = res.outcome?.prefill || {};
              setForm((prev) => ({
                ...prev,
                causeOfCallId: pre.scheme_cause_of_call_id ? String(pre.scheme_cause_of_call_id) : prev.causeOfCallId,
                description: pre.description || res.outcome?.advice || prev.description,
              }));
            }}
          />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Status Dropdown */}
          <div >
            <label className="block text-sm font-normal text-black mb-1">Status</label>
            <select name="status" value={form.status} onChange={handleChange} className="drop-black w-full px-4 py-2.5 border rounded-md text-base" disabled>
              <option value="Draft">Draft</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          {/* Cause of Call Dropdown */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Cause of Call<span className="text-red-500">*</span></label>
            <select name="causeOfCallId" value={form.causeOfCallId} onChange={handleChange} className="drop-black w-full px-3 py-2.5 border rounded-md text-base" required>
              {loadingCauses ? (
                <option>Loading...</option>
              ) : (
                causeOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))
              )}
            </select>
            {/* Other Reason Text Field - Show when "Others" is selected */}
            {isOthersSelected && (
              <div className="mt-2">
                <label className="block text-sm font-normal text-black mb-1">Other Reason<span className="text-red-500">*</span></label>
                <textarea
                  name="otherReason"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  className="w-full px-3 py-2.5 border rounded-md text-base min-h-[80px]"
                  placeholder="Please specify the other reason..."
                  required
                />
              </div>
            )}
          </div>
          {/* Location Dropdown */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Location<span className="text-red-500">*</span></label>
            <select name="location" value={form.location} onChange={handleChange} className="drop-black w-full px-4 py-2.5 border rounded-md text-base" required>
              {LOCATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {/* Vehicle Regn No. Field */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Vehicle Regn No.</label>
            <input
              type="text"
              value={(() => {
                if (loadingVehicle || loadingCustomer) return 'Loading...';
                if (vehicleDetails?.vehicle) return vehicleDetails.vehicle.registration_number;
                if (selectedVehicle) return selectedVehicle.registration_number;
                return 'No vehicle found';
              })()}
              className="w-full px-4 py-2.5 border rounded bg-gray-100 text-base"
              disabled
            />
          </div>
          {/* Vehicle Make/Model (auto-filled) */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Vehicle Make/Model</label>
            <input
              type="text"
              value={selectedVehicle ? `${selectedVehicle.make || ''} ${selectedVehicle.model || ''}`.trim() : ''}
              className="w-full px-4 py-2.5 border rounded bg-gray-100 text-base"
              disabled
            />
          </div>
          {/* Policy Field */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Policy</label>
            <input
              type="text"
              value={(() => {
                if (loadingPolicy || loadingCustomer) return 'Loading...';
                if (selectedPolicy) return selectedPolicy.policy_number || '';
                return 'No policy found';
              })()}
              className="w-full px-4 py-2.5 border rounded bg-gray-100 text-base"
              disabled
            />
          </div>
          {/* Call Received Date */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Call Received Date</label>
            <input type="datetime-local" name="callReceivedDate" value={form.callReceivedDate} onChange={handleChange} className=" outline-none w-full px-4 py-2.5 border rounded-md text-base" required />
          </div>
          {/* Kms at Breakdown - Commented out as not required */}
          {/* <div>
            <label className=" text-sm font-normal text-black mb-1">Kms at Breakdown</label>
            <input type="number" name="kmsAtBreakdown" value={form.kmsAtBreakdown} onChange={handleChange} className="w-full outline-none px-4 py-2.5 border rounded-md text-base" />
          </div> */}
          {/* Reference ID */}
          <div>
            <label className="block text-sm font-normal text-black mb-1">Reference ID</label>
            <input type="text" name="referenceId" value={form.referenceId} onChange={handleChange} className="w-full outline-none px-4 py-2.5 border rounded-md text-base" />
          </div>
          {/* Description */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-normal text-black mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} className="w-full px-4 outline-none py-2.5 border rounded-md text-base min-h-[80px]" />
          </div>
        </div>
        <div className="flex justify-center gap-6 mt-8 pb-6">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 rounded-lg text-lg text-gray-700 hover:bg-gray-50"
            onClick={() => navigate('/case-booking')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-scarlet-600 text-white rounded-lg text-lg"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>

      {/* Enhanced Policy Limit Confirmation Dialog */}
      {showPolicyLimitDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="ml-3 text-lg font-medium text-gray-900">Policy Claims Limit Exceeded</h3>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">
                  The policy has reached its maximum claims limit. This means no more claims can be processed under this policy.
                </p>
                <p className="text-sm text-gray-600 font-medium">
                  Do you want to proceed with creating the case anyway? This will bypass the policy limit validation.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handlePolicyLimitCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePolicyLimitConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  Proceed Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCase; 